import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  CheckCircle2,
  Cloud,
  CloudOff,
  Loader2,
  WifiOff,
} from 'lucide-react';
import {
  getEngineSnapshot,
  kickEngine,
  subscribeEngine,
  type EngineState,
} from '../sync/engine';

/**
 * Persistent header showing sync state + queued count + failed count.
 * Subscribes to the sync engine snapshot — no Dexie polling here, the
 * engine pushes updates as it drains.
 */
export function SyncStatusBar() {
  const [snap, setSnap] = useState(getEngineSnapshot);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => subscribeEngine(setSnap), []);

  useEffect(() => {
    const onChange = () => setOnline(navigator.onLine);
    window.addEventListener('online', onChange);
    window.addEventListener('offline', onChange);
    return () => {
      window.removeEventListener('online', onChange);
      window.removeEventListener('offline', onChange);
    };
  }, []);

  const ageMins =
    snap.last_sync_at != null
      ? Math.floor((Date.now() - snap.last_sync_at) / 60_000)
      : null;

  const iconTone = colorForState(snap.state, online);
  const left = leftLabel(snap, online);
  const right = rightLabel(snap, ageMins);

  // Failed state is interactive — tap to retry.
  const interactive = snap.state === 'failed' || snap.queued > 0;

  return (
    <div
      className={clsx(
        'sticky top-0 z-30 px-3 py-1.5 text-xs flex items-center justify-between',
        'border-b border-mono-800 bg-mono-900/95 backdrop-blur',
        interactive && 'cursor-pointer active:bg-mono-800',
      )}
      role="status"
      aria-live="polite"
      onClick={interactive ? () => kickEngine() : undefined}
    >
      <div className="flex items-center gap-2">
        <StatusIcon state={snap.state} online={online} className={iconTone} />
        <span>{left}</span>
      </div>
      <div className="text-mono-500">{right}</div>
    </div>
  );
}

function StatusIcon({ state, online, className }: { state: EngineState; online: boolean; className: string }) {
  const cls = clsx('h-3.5 w-3.5', className);
  if (!online) return <WifiOff className={cls} strokeWidth={2} />;
  if (state === 'failed') return <CloudOff className={cls} strokeWidth={2} />;
  if (state === 'syncing') return <Loader2 className={clsx(cls, 'animate-spin')} strokeWidth={2} />;
  // idle/online states
  if (state === 'idle') return <CheckCircle2 className={cls} strokeWidth={2} />;
  return <Cloud className={cls} strokeWidth={2} />;
}

function colorForState(state: EngineState, online: boolean): string {
  if (!online) return 'text-accent-amber';
  if (state === 'failed') return 'text-accent-red';
  if (state === 'syncing') return 'text-cyan-400';
  return 'text-accent-green';
}

function leftLabel(
  snap: ReturnType<typeof getEngineSnapshot>,
  online: boolean,
): string {
  if (!online) {
    return snap.queued > 0
      ? `Offline · ${snap.queued} queued`
      : 'Offline';
  }
  if (snap.state === 'syncing') {
    return `Syncing ${snap.queued}…`;
  }
  if (snap.state === 'failed') {
    return `Sync failed · ${snap.failed} need attention`;
  }
  if (snap.queued > 0) {
    return `Online · ${snap.queued} queued`;
  }
  return 'Online · 0 queued';
}

function rightLabel(
  snap: ReturnType<typeof getEngineSnapshot>,
  ageMins: number | null,
): string {
  if (snap.state === 'failed') return 'tap to retry';
  if (snap.queued > 0 && navigator.onLine) return 'tap to sync';
  if (ageMins == null) return 'never synced';
  if (ageMins === 0) return 'synced just now';
  return `synced ${ageMins}m ago`;
}
